<script setup>
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from '@/components/ui/toast'
import ReportView from '@/components/report/ReportView.vue'

const route = useRoute()
const store = useStore()

const orgId = computed(() => route.params.org_id)
const sessionUuid = computed(() => route.params.session_code)
const reportUuid = computed(() => route.query.report)
const versionQuery = computed(() => route.query.version ? parseInt(route.query.version, 10) : null)

const currentReport = computed(() => {
  if (!reportUuid.value) return null
  return store.getters['report/getReport'](orgId.value, reportUuid.value)
})

// Fetch report with optional version
const fetchReportWithVersion = async (uuid, version = null) => {
  try {
    await store.dispatch('report/fetchReport', {
      orgId: orgId.value,
      reportUuid: uuid,
      version: version
    })
  } catch (error) {
    console.error('Error fetching report:', error)
    toast({
      title: 'Error',
      description: 'Failed to load report',
      variant: 'destructive'
    })
  }
}

// Watch for report UUID changes
watch(reportUuid, async (newUuid) => {
  if (newUuid) {
    store.dispatch('report/setActiveReport', newUuid)
    store.dispatch('report/setActiveVersion', versionQuery.value)
    await fetchReportWithVersion(newUuid, versionQuery.value)
  }
}, { immediate: true })

// Watch for version query param changes
watch(versionQuery, async (newVersion, oldVersion) => {
  if (reportUuid.value && newVersion !== oldVersion) {
    store.dispatch('report/setActiveVersion', newVersion)
    await fetchReportWithVersion(reportUuid.value, newVersion)
  }
})

// Handle new version created from text selection
const handleVersionCreated = async () => {
  // The edit request has been sent - the new version will be created
  // after the LLM responds. Show a toast to indicate the request was sent.
  toast({
    title: 'Edit request sent',
    description: 'The report will be updated after the AI processes your request',
  })
  // Note: The report will be refreshed when the user navigates back or
  // when we implement WebSocket notifications for report version changes
}

const handleReportUpdated = (updatedReport) => {
  store.dispatch('report/updateReport', {
    orgId: orgId.value,
    report: updatedReport
  })
}
</script>

<template>
  <div class="h-full">
    <ReportView
      v-if="currentReport"
      :report="currentReport"
      :session-uuid="sessionUuid"
      :organization-id="Number(orgId)"
      enable-text-selection
      @version-created="handleVersionCreated"
      @report-updated="handleReportUpdated"
    />
    <div v-else class="h-full flex items-center justify-center text-muted-foreground">
      <p>Loading report...</p>
    </div>
  </div>
</template>
